// src/context/CartContext.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";

const STORAGE_KEY = "kaida_cart_v1";

const CartContext = createContext(null);

const load = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * Cart lines hold a snapshot of the product for display only.
 * Price and stock are re-read from Firestore inside placeOrder,
 * so a stale localStorage cart can never set the price.
 */
const reducer = (state, action) => {
  switch (action.type) {
    case "ADD": {
      const { product, qty } = action;
      const existing = state.find((l) => l.id === product.id);
      const cap = Number(product.stock) || 0;

      if (existing) {
        return state.map((l) =>
          l.id === product.id ? { ...l, qty: Math.min(l.qty + qty, cap) } : l,
        );
      }
      return [
        ...state,
        {
          id: product.id,
          nameSq:
            product.nameSq?.trim() || product.nameDe?.trim() || product.pmuCode,
          pmuCode: product.pmuCode,
          hex: product.hex,
          price: Number(product.price) || 0,
          image: product.images?.[0] || null,
          stock: cap,
          qty: Math.min(qty, cap),
        },
      ];
    }

    case "SET_QTY":
      return state
        .map((l) =>
          l.id === action.id
            ? { ...l, qty: Math.max(1, Math.min(action.qty, l.stock || 99)) }
            : l,
        )
        .filter((l) => l.qty > 0);

    case "REMOVE":
      return state.filter((l) => l.id !== action.id);

    case "CLEAR":
      return [];

    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  const [items, dispatch] = useReducer(reducer, undefined, load);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* private mode / quota — cart just won't persist */
    }
  }, [items]);

  // lock body scroll while the drawer is open
  useEffect(() => {
    document.body.classList.toggle("no-scroll", isOpen);
    return () => document.body.classList.remove("no-scroll");
  }, [isOpen]);

  const value = useMemo(() => {
    const count = items.reduce((n, l) => n + l.qty, 0);
    const subtotal = items.reduce((n, l) => n + l.price * l.qty, 0);

    return {
      items,
      count,
      subtotal,
      isOpen,
      openCart: () => setIsOpen(true),
      closeCart: () => setIsOpen(false),
      add: (product, qty = 1) => {
        // Samples are request-only — they must never reach checkout
        if (product.isSample) return false;
        if ((Number(product.stock) || 0) < 1) return false;
        dispatch({ type: "ADD", product, qty });
        setIsOpen(true);
        return true;
      },
      setQty: (id, qty) => dispatch({ type: "SET_QTY", id, qty }),
      remove: (id) => dispatch({ type: "REMOVE", id }),
      clear: () => dispatch({ type: "CLEAR" }),
    };
  }, [items, isOpen]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
  return ctx;
};
